import { supabase } from '../config/supabase.js';
export class OrderManagementService {
    /**
     * Create or update a medication order from provider approval
     * This replaces the fax-based order creation workflow
     */
    static async createOrUpdateOrderFromApproval(approvalId, overrides = {}, changeReason) {
        console.log('🔍 ORDER SERVICE: Creating/updating order from approval:', approvalId);
        try {
            // First, check if an order already exists for this approval
            const { data: existingOrders, error: searchError } = await supabase
                .from('medication_orders')
                .select('*')
                .eq('approval_id', approvalId);
            if (searchError) {
                console.error('❌ ORDER SERVICE: Error searching for existing orders:', searchError);
                return {
                    success: false,
                    error: `Failed to search for existing orders: ${searchError.message}`
                };
            }
            const existingOrder = existingOrders?.[0];
            if (existingOrder) {
                // Update existing order
                console.log('🔄 ORDER SERVICE: Updating existing order:', existingOrder.id);
                return await OrderManagementService.updateOrder(existingOrder.id, overrides, changeReason);
            }
            else {
                // Create new order
                console.log('➕ ORDER SERVICE: Creating new order for approval:', approvalId);
                return await OrderManagementService.createOrderFromApproval(approvalId, overrides);
            }
        }
        catch (error) {
            console.error('❌ ORDER SERVICE: Exception in createOrUpdateOrderFromApproval:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Create a new medication order from approval data
     */
    static async createOrderFromApproval(approvalId, overrides = {}) {
        try {
            // Get approval details
            const { data: approval, error: approvalError } = await supabase
                .from('medication_approvals')
                .select(`
          *,
          patient_medication_preferences (
            *,
            medications (*),
            patients (*),
            medication_dosages (*)
          )
        `)
                .eq('id', approvalId)
                .single();
            if (approvalError || !approval) {
                console.error('❌ ORDER SERVICE: Approval not found:', approvalError);
                return {
                    success: false,
                    error: 'Medication approval not found'
                };
            }
            const preference = approval.patient_medication_preferences;
            const medication = preference.medications;
            const patient = preference.patients;
            const dosage = preference.medication_dosages;
            // Get provider profile ID
            const { data: provider, error: providerError } = await supabase
                .from('providers')
                .select('profile_id')
                .eq('id', approval.provider_id)
                .single();
            if (providerError || !provider) {
                console.error('❌ ORDER SERVICE: Provider not found:', providerError);
                return {
                    success: false,
                    error: 'Provider not found'
                };
            }
            // Calculate order details
            const quantity = overrides.quantity || 1;
            const unitPrice = overrides.unit_price || dosage?.price || medication.unit_price || 0;
            const totalAmount = quantity * unitPrice;
            // Create order
            const orderData = {
                approval_id: approvalId,
                patient_id: patient.id,
                medication_id: medication.id,
                quantity,
                unit_price: unitPrice,
                total_amount: totalAmount,
                payment_status: overrides.payment_status || 'pending',
                payment_method: overrides.payment_method,
                fulfillment_status: overrides.fulfillment_status || 'pending',
                tracking_number: overrides.tracking_number,
                shipped_date: overrides.shipped_date,
                estimated_delivery: overrides.estimated_delivery,
                admin_notes: overrides.admin_notes,
                sent_to_pharmacy: overrides.sent_to_pharmacy,
                provider_profile_id: provider.profile_id,
                patient_profile_id: patient.profile_id
            };
            const { data: order, error: createError } = await supabase
                .from('medication_orders')
                .insert(orderData)
                .select()
                .single();
            if (createError) {
                console.error('❌ ORDER SERVICE: Failed to create order:', createError);
                return {
                    success: false,
                    error: `Failed to create order: ${createError.message}`
                };
            }
            console.log('✅ ORDER SERVICE: Order created successfully:', order.id);
            // Log the creation
            await OrderManagementService.logOrderChange(order.id, 'order_created', null, 'created', provider.profile_id, 'Order created from medication approval');
            return {
                success: true,
                data: order
            };
        }
        catch (error) {
            console.error('❌ ORDER SERVICE: Exception creating order:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Update an existing medication order
     */
    static async updateOrder(orderId, updates, changeReason) {
        console.log('🔄 ORDER SERVICE: Updating order:', orderId);
        try {
            // Get current order for audit trail
            const { data: currentOrder, error: fetchError } = await supabase
                .from('medication_orders')
                .select('*')
                .eq('id', orderId)
                .single();
            if (fetchError || !currentOrder) {
                console.error('❌ ORDER SERVICE: Order not found for update:', fetchError);
                return {
                    success: false,
                    error: 'Order not found'
                };
            }
            // Get current user for audit log
            const { data: { user } } = await supabase.auth.getUser();
            const changedBy = user?.id || 'system';
            // Prepare update data and track changes
            const updateData = {
                ...updates,
                updated_at: new Date().toISOString()
            };
            // Recalculate total if quantity or unit_price changed
            if (updates.quantity || updates.unit_price) {
                const newQuantity = updates.quantity ?? currentOrder.quantity;
                const newUnitPrice = updates.unit_price ?? currentOrder.unit_price;
                updateData.total_amount = newQuantity * newUnitPrice;
            }
            // Update the order
            const { data: updatedOrder, error: updateError } = await supabase
                .from('medication_orders')
                .update(updateData)
                .eq('id', orderId)
                .select()
                .single();
            if (updateError) {
                console.error('❌ ORDER SERVICE: Failed to update order:', updateError);
                return {
                    success: false,
                    error: `Failed to update order: ${updateError.message}`
                };
            }
            // Log all changes
            for (const [field, newValue] of Object.entries(updates)) {
                if (field in currentOrder) {
                    const oldValue = currentOrder[field];
                    if (oldValue !== newValue) {
                        await OrderManagementService.logOrderChange(orderId, field, String(oldValue), String(newValue), changedBy, changeReason);
                    }
                }
            }
            console.log('✅ ORDER SERVICE: Order updated successfully');
            return {
                success: true,
                data: updatedOrder
            };
        }
        catch (error) {
            console.error('❌ ORDER SERVICE: Exception updating order:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Get order by ID
     */
    static async getOrder(orderId) {
        try {
            const { data: order, error } = await supabase
                .from('medication_orders')
                .select(`
          *,
          medications (name, generic_name),
          patients (first_name, last_name)
        `)
                .eq('id', orderId)
                .single();
            if (error) {
                if (error.code === 'PGRST116') {
                    return { success: true, data: null };
                }
                return {
                    success: false,
                    error: `Failed to fetch order: ${error.message}`
                };
            }
            return { success: true, data: order };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Get orders for a patient
     */
    static async getPatientOrders(patientId) {
        try {
            const { data: orders, error } = await supabase
                .from('medication_orders')
                .select(`
          *,
          medications (name, generic_name)
        `)
                .eq('patient_id', patientId)
                .order('created_at', { ascending: false });
            if (error) {
                return {
                    success: false,
                    error: `Failed to fetch patient orders: ${error.message}`
                };
            }
            return { success: true, data: orders || [] };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Get order audit trail
     */
    static async getOrderAuditLog(orderId) {
        try {
            const { data: logs, error } = await supabase
                .from('order_audit_logs')
                .select('*')
                .eq('order_id', orderId)
                .order('created_at', { ascending: false });
            if (error) {
                return {
                    success: false,
                    error: `Failed to fetch order audit log: ${error.message}`
                };
            }
            return { success: true, data: logs || [] };
        }
        catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Log order changes for audit trail
     */
    static async logOrderChange(orderId, fieldName, oldValue, newValue, changedBy, changeReason) {
        try {
            await supabase
                .from('order_audit_logs')
                .insert({
                order_id: orderId,
                field_name: fieldName,
                old_value: oldValue,
                new_value: newValue,
                changed_by: changedBy,
                change_reason: changeReason
            });
        }
        catch (error) {
            console.error('⚠️ ORDER SERVICE: Failed to log order change:', error);
            // Don't fail the main operation if audit logging fails
        }
    }
    /**
     * Process provider approval by creating/updating order
     * This is the main entry point that replaces fax-based order creation
     */
    static async processProviderApproval(approvalId, pharmacyDetails, overrides = {}) {
        console.log('🔍 ORDER SERVICE: Processing provider approval:', approvalId);
        try {
            // Check if order already exists
            const { data: existingOrders } = await supabase
                .from('medication_orders')
                .select('id')
                .eq('approval_id', approvalId);
            const isNewOrder = !existingOrders || existingOrders.length === 0;
            // Create or update order
            const orderResult = await OrderManagementService.createOrUpdateOrderFromApproval(approvalId, {
                ...overrides,
                sent_to_pharmacy: overrides.sent_to_pharmacy || new Date().toISOString(),
                admin_notes: overrides.admin_notes ||
                    (pharmacyDetails ? `Sent to ${pharmacyDetails.pharmacy_name} (${pharmacyDetails.fax_number})` : undefined)
            }, 'Provider approval processed');
            if (!orderResult.success || !orderResult.data) {
                return {
                    success: false,
                    error: orderResult.error || 'Failed to process order'
                };
            }
            console.log(`✅ ORDER SERVICE: Provider approval processed - ${isNewOrder ? 'Created' : 'Updated'} order:`, orderResult.data.id);
            return {
                success: true,
                data: {
                    order: orderResult.data,
                    isNewOrder
                }
            };
        }
        catch (error) {
            console.error('❌ ORDER SERVICE: Exception processing provider approval:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
}
